import { ActionIcon, Anchor, Group, Stack, Tooltip } from "@mantine/core";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useInstatusStatus } from "@/api/resources/instatus/hooks";
import {
  InstatusIncidentStatus,
  type InstatusActiveIncident,
  type InstatusActiveMaintenance,
} from "@/api/resources/instatus/types";
import {
  getInstatusMaintenanceEndDate,
  getLatestInstatusIncident,
  getLatestInstatusMaintenance,
} from "@/api/resources/instatus/utils";
import { SYSTEM_COLORS } from "@/configs/colorScheme";
import { INSTATUS_MESSAGE_AUTOCLOSE } from "@/configs/instatus";
import { useNotifications } from "@/hooks/useNotifications";
import { getDateFormatted } from "@/utils/dates";
import Icon from "@/utils/icons";

export const AppStatusBanner = () => {
  const { t } = useTranslation();
  const notify = useNotifications();

  const { data: instatusStatus, isLoading: loadingInstatusStatus } = useInstatusStatus();
  const latestMaintenance = instatusStatus ? getLatestInstatusMaintenance(instatusStatus) : null;
  const latestIncident = instatusStatus ? getLatestInstatusIncident(instatusStatus) : null;

  const showLatestIncidentNotification = useCallback(
    (incident: InstatusActiveIncident) => {
      const message = (
        <Stack gap={2}>
          {t(`instatus.message.incident.${incident.status}`, {
            impact: t(`instatus.status.${incident.impact}` as never),
          })}
          <Anchor mx={0} c="inherit" component="a" href={incident.url} target="_blank" rel="noreferrer">
            <Group wrap="nowrap" gap={2}>
              {t("show-more")} <Icon.GO_FORWARD size={16} />
            </Group>
          </Anchor>
        </Stack>
      );
      if (
        incident.status === InstatusIncidentStatus.RESOLVED ||
        incident.status === InstatusIncidentStatus.MONITORING
      ) {
        notify.success(message, t("instatus.type.incident"), {
          autoClose: INSTATUS_MESSAGE_AUTOCLOSE,
        });
      } else {
        notify.error(message, t("instatus.type.incident"), {
          autoClose: INSTATUS_MESSAGE_AUTOCLOSE,
        });
      }
    },
    [notify, t],
  );

  const showLatestMaintenanceNotification = useCallback(
    (maintenance: InstatusActiveMaintenance) => {
      notify.warning(
        <Stack gap={2}>
          {t(`instatus.message.maintenance.${maintenance.status}`, {
            date: getDateFormatted(maintenance.start, "datetime-short"),
            end_date: getDateFormatted(getInstatusMaintenanceEndDate(maintenance), "datetime-short"),
          })}
          <Anchor mx={0} c="inherit" component="a" href={maintenance.url} target="_blank" rel="noreferrer">
            <Group wrap="nowrap" gap={2}>
              {t("show-more")} <Icon.GO_FORWARD size={16} />
            </Group>
          </Anchor>
        </Stack>,
        t("instatus.type.maintenance"),
        {
          autoClose: INSTATUS_MESSAGE_AUTOCLOSE,
        },
      );
    },
    [notify, t],
  );

  useEffect(() => {
    if (!loadingInstatusStatus && latestIncident) {
      showLatestIncidentNotification(latestIncident);
    }
  }, [latestIncident, showLatestIncidentNotification, loadingInstatusStatus]);

  useEffect(() => {
    if (!loadingInstatusStatus && latestMaintenance) {
      showLatestMaintenanceNotification(latestMaintenance);
    }
  }, [latestMaintenance, showLatestMaintenanceNotification, loadingInstatusStatus]);

  if (loadingInstatusStatus) return null;
  if (!latestIncident && !latestMaintenance) return null;

  return (
    <Group wrap="nowrap" gap={2}>
      {latestIncident && (
        <Tooltip label={t("instatus.type.incident")}>
          <ActionIcon variant="transparent" onClick={() => showLatestIncidentNotification(latestIncident)}>
            <Icon.ERROR size={18} color={SYSTEM_COLORS.error} />
          </ActionIcon>
        </Tooltip>
      )}
      {latestMaintenance && (
        <Tooltip label={t("instatus.type.maintenance")}>
          <ActionIcon variant="transparent" onClick={() => showLatestMaintenanceNotification(latestMaintenance)}>
            <Icon.INSTALLATION size={18} color={SYSTEM_COLORS.warning} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

export default AppStatusBanner;
